/**
 * Registry that lets a mounted product page take over an agent-started
 * try-on for its own product, so the mirror opens inline on the page instead
 * of in the fallback popup.
 *
 * A product page registers `{ open, close }` for its product id while it is
 * mounted. The avatar widget's try-on poller asks `claimInlineTryOn` first
 * and only falls back to the popup when no page claims the product.
 */

const handlers = new Map();

const key = (productId) => String(productId ?? "");

/**
 * @param {string} productId
 * @param {{ open: (opts: { autoStart: string|null }) => void, close: () => void }} handler
 * @returns {() => void} unregister
 */
export function registerInlineTryOn(productId, handler) {
  const k = key(productId);
  if (!k) return () => {};
  handlers.set(k, handler);
  return () => {
    if (handlers.get(k) === handler) handlers.delete(k);
  };
}

/** Open the try-on inline if a page for this product is mounted. */
export function claimInlineTryOn(productId, opts = {}) {
  const h = handlers.get(key(productId));
  if (!h) return false;
  h.open({ autoStart: opts.autoStart ?? null });
  return true;
}

/** Close every inline mirror (the agent said "close the try-on"). */
export function closeInlineTryOns() {
  handlers.forEach((h) => h.close());
}

/** True when the current route is that product's own detail page. */
export function isProductRoute(productId) {
  const k = key(productId);
  if (!k) return false;
  const path = window.location.pathname;
  return path === `/item/${k}` || path === `/product/${k}`;
}
